import { Subscription } from 'rxjs';
import { EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';

import { browserLanguage, convertToBoolean, convertToInt, isExternalLink, isTypeof, thfLocaleDefault } from './../../utils/util';

import { ThfPageLogin } from './interfaces/thf-page-login.interface';
import { ThfPageLoginAuthenticationType } from './enums/thf-page-login-authentication-type.enum';
import { ThfPageLoginCustomField } from './interfaces/thf-page-login-custom-field.interface';
import { ThfPageLoginLiterals } from './interfaces/thf-page-login-literals.interface';
import { ThfPageLoginRecovery } from './interfaces/thf-page-login-recovery.interface';
import { ThfPageLoginService } from './thf-page-login.service';

const thfPageLoginContentMaxLength = 40;

export const thfPageLoginLiteralsDefault = {
  en: <ThfPageLoginLiterals> {
    title: 'Welcome',
    loginErrorPattern: 'Invalid Login',
    loginHint: `Your login user was given to you at your first day.
    If you don't have this information contact support`,
    loginPlaceholder: 'Insert your e-mail',
    passwordErrorPattern: 'Invalid Password',
    passwordPlaceholder: 'Insert your password',
    customFieldErrorPattern: 'Invalid value',
    customFieldPlaceholder: 'Please enter a value',
    rememberUser: 'Automatic login',
    rememberUserHint: 'You can disable this option in system configuration',
    submitLabel: 'Enter',
    submittedLabel: 'Loading...',
    forgotPassword: 'Forgot your Password?',
    highlightInfo: '',
    registerUrl: 'New register',
    titlePopover: 'Oops!',
    forgotYourPassword: 'Forgot your password?',
    ifYouTryHarder: 'If you try ',
    attempts: '{0} more time(s) ',
    yourUserWillBeBlocked: 'without success your user will be blocked and you will be left 24 hours without being able to access :(',
    createANewPasswordNow: 'Better create a new password now! You will be able to log into the system right away.',
    iForgotMyPassword: 'I forgot my password'
  },
  es: <ThfPageLoginLiterals> {
    title: 'Bienvenido',
    loginErrorPattern: 'Login invÃ¡lido',
    loginHint: `Su usuario ha sido entregado para usted en su primer dÃ­a.
    Si no tiene esta informaciÃ³n, pÃ³ngase en contacto con el soporte tÃ©cnico`,
    loginPlaceholder: 'Inserte su e-mail',
    passwordErrorPattern: 'ContraseÃ±a invÃ¡lida',
    passwordPlaceholder: 'Inserte su contraseÃ±a',
    customFieldErrorPattern: 'Valor no vÃ¡lido.',
    customFieldPlaceholder: 'Por favor introduzca un valor',
    rememberUser: 'Inicio de sesiÃ³n automÃ¡ticamente',
    rememberUserHint: 'Puede deshabilitar esta opciÃ³n en el menÃº del sistema.',
    submitLabel: 'Entrar',
    submittedLabel: 'Cargando...',
    forgotPassword: 'Olvidaste tu contraseÃ±a?',
    highlightInfo: '',
    registerUrl: 'Nuevo registro',
    titlePopover: 'Opa!',
    forgotYourPassword: 'Olvidaste tu contraseÃ±a?',
    ifYouTryHarder: 'Si intenta mÃ¡s ',
    attempts: '{0} vez/veces ',
    yourUserWillBeBlocked: 'sin Ã©xito su usuario sera bloqueado y usted vÃ¡s permanecer 24 horas sin poder acceder a :(',
    createANewPasswordNow: 'Â¡Mejor crear una nueva contraseÃ±a ahora! Usted podrÃ¡ entrar en el sistema inmediatamente despuÃ©s.',
    iForgotMyPassword: 'Olvide mi contraseÃ±a'
  },
  pt: <ThfPageLoginLiterals> {
    title: 'Bem-vindo',
    loginErrorPattern: 'Login invÃ¡lido',
    loginHint: `Seu usuÃ¡rio foi entregue a vocÃª no seu primeiro dia.
    Caso nÃ£o tenha mais essa informaÃ§Ã£o contacte o suporte`,
    loginPlaceholder: 'Insira seu e-mail',
    passwordErrorPattern: 'Senha invÃ¡lida',
    passwordPlaceholder: 'Insira sua senha',
    customFieldErrorPattern: 'Valor invÃ¡lido.',
    customFieldPlaceholder: 'Por favor insira um valor',
    rememberUser: 'Logar automaticamente',
    rememberUserHint: 'VocÃª pode desabilitar essa opÃ§Ã£o no menu do sistema',
    submitLabel: 'Entrar',
    submittedLabel: 'Carregando...',
    forgotPassword: 'Esqueceu sua senha?',
    highlightInfo: '',
    registerUrl: 'Novo registro',
    titlePopover: 'Opa!',
    forgotYourPassword: 'Esqueceu sua senha?',
    ifYouTryHarder: 'Se tentar mais ',
    attempts: '{0} vez(es) ',
    yourUserWillBeBlocked: 'sem sucesso seu usuÃ¡rio serÃ¡ bloqueado e vocÃª fica 24 horas sem poder acessar :(',
    createANewPasswordNow: 'Melhor criar uma senha nova agora! VocÃª vai poder entrar no sistema logo em seguida.',
    iForgotMyPassword: 'Esqueci minha senha'
  }
};

export const thfPageLoginLiteralIn = {
  en: 'in',
  es: 'en',
  pt: 'em'
};

export const thfPageLoginLiteralTo = {
  en: 'to',
  es: 'al',
  pt: 'ao',
};

/**
 * @description
 *
 * O componente `thf-page-login` Ã© utilizado como template para tela de login.
 * Com ele Ã© possÃ­vel definirmos valores para usuÃ¡rio, senha e definir aÃ§Ãµes para recuperaÃ§Ã£o de senha e gravaÃ§Ã£o de dados do usuÃ¡rio.
 * TambÃ©m Ã© possÃ­vel inserir uma imagem em conjunto com um texto de destaque.
 *
 *
 * A propriedade `t-authentication-url` automatiza a rotina do componente e simplifica o processo para autenticaÃ§Ã£o do usuÃ¡rio, bastando
 * definir uma url para requisiÃ§Ã£o da autenticaÃ§Ã£o. A flexibilidade e praticidade podem chegar a um nÃ­vel em que o desenvolvimento
 * da aplicaÃ§Ã£o no *client side* Ã© desprovida de qualquer cÃ³digo-fonte relacionado Ã  rotina de login de usuÃ¡rio.
 * Seu detalhamento para uso pode ser visto logo abaixo em *propriedades*.
 * Caso julgue necessÃ¡rio, pode-se tambÃ©m definir manualmente a rotina do componente.
 *
 *
 * Para que as imagens sejam exibidas corretamente, Ã© necessÃ¡rio incluir o caminho delas ao projeto. Para isso, edite
 * o *assets* no arquivo **angular.json** da aplicaÃ§Ã£o na seguinte ordem:
 * ```
 *   "assets": [
 *     "src/assets",
 *     "src/favicon.ico",
 *     {
 *       "glob": "**\/*",
 *       "input": "node_modules/@totvs/thf-theme/images",
 *       "output": "assets/images"
 *     }
 *   ]
 * ```
 */
export abstract class ThfPageLoginBaseComponent implements OnDestroy {

  allLoginErrors: Array<string> = [];
  allPasswordErrors: Array<string> = [];
  containsCustomLiterals: boolean = false;
  customFieldObject: ThfPageLoginCustomField;
  customFieldType: string;
  customizedDefaultLiterals: ThfPageLoginLiterals = {};
  loginSubscription: Subscription;
  password: string;
  selectedLanguage: string;
  showExceededAttemptsWarning = false;

  private _authenticationType: ThfPageLoginAuthenticationType = ThfPageLoginAuthenticationType.Basic;
  private _authenticationUrl: string;
  private _blockedUrl: string;
  private _contactEmail: string;
  private _customField: string | ThfPageLoginCustomField;
  private _environment?: string;
  private _exceededAttemptsWarning?: number;
  private _hideRememberUser: boolean = false;
  private _literals: ThfPageLoginLiterals;
  private _loading?: boolean = false;
  private _login: string;
  private _loginErrors: Array<string> = [];
  private _passwordErrors: Array<string> = [];
  private _productName: string;
  private _recovery: string | ThfPageLoginRecovery | Function;
  private _registerUrl: string;
  private _urlRecovery: string;

  protected rememberUser: boolean = false;

  /**
   * @optional
   *
   * @description
   *
   * Ao informar um valor do tipo `string`, o mesmo serÃ¡ aplicado como a chave do campo customizado e utilizarÃ¡
   * os valores padrÃµes contidos na propriedade `literals` como `customFieldErrorPattern` e `customFieldPlaceholder`.
   *
   * Existe a possibilidade de informar um objeto que segue a definiÃ§Ã£o da interface `ThfPageLoginCustomField`, onde
   * atravÃ©s dos parÃ¢metros enviados pode gerar um `thf-input`, `thf-combo` especificamente para serviÃ§os
   * ou `thf-select` para valores fixos.
   *
   * Abaixo seguem os exemplos de cada tipo de campo.
   *
   * `thf-input`:
   *
   * ```
   * {
   *   property: 'domain',
   *   value: 'jv01',
   *   placeholder: 'Enter your domain',
   *   pattern: '[a-z]',
   *   errorPattern: 'Invalid value',
   * }
   * ```
   *
   * `thf-combo`:
   *
   * ```
   * {
   *   property: 'domain',
   *   value: 'jv01',
   *   placeholder: 'Enter your domain',
   *   url: 'https://thf.totvs.com.br/sample/api/comboOption/domains',
   *   fieldValue: 'nickname'
   * }
   * ```
   *
   * `thf-select`:
   *
   * ```
   * {
   *   property: 'domain',
   *   value: 'jv01',
   *   placeholder: 'Enter your domain',
   *   options: [{label: 'Domain 1', value: '1'}, {label: 'Domain 2', value: '2'}]
   * }
   * ```
   *
   * Caso o customField possua options, url e fieldValue preenchidos, serÃ¡ priorizado o thf-select
   * utilizando o options.
   *
   */
  @Input('t-custom-field') set customField(value: string | ThfPageLoginCustomField) {
    if (value) {

      if (isTypeof(value, 'string')) {
        this.customFieldType = 'input';
        this._customField = value;
        this.customFieldObject = this.getDefaultCustomFieldObject(value);
        return;
      }

      if (isTypeof(value, 'object') && !Array.isArray(value) && value['property']) {
        this._customField = value;
        this.customFieldObject = <ThfPageLoginCustomField> value;

        if (!this.customFieldObject.options && !this.customFieldObject.url) {
          this.customFieldType = 'input';
        } else {
          this.customFieldType = this.customFieldObject.options ? 'select' : 'combo';
        }

        return;
      }

    }

    this._customField = undefined;
    this.customFieldObject = undefined;
  }

  get customField(): string | ThfPageLoginCustomField {
    return this._customField;
  }

  /**
   * O `t-background` permite inserir uma imagem de destaque ao lado direito do formulÃ¡rio de login, caso a propriedade
   * nÃ£o seja preenchida o formulÃ¡rio serÃ¡ centralizado no espaÃ§o disponÃ­vel.
   *
   * A fonte da imagem pode ser de um caminho local ou uma url de um servidor externo.
   *
   * AlÃ©m da imagem, Ã© possÃ­vel adicionar um texto informativo por cima da imagem da imagem de destaque, para isso informe
   * um valor para a literal `highlightInfo`.
   *
   * > Veja mais sobre as literais na propriedade `t-literals`.
   *
   * Exemplos de valores vÃ¡lidos:
   * - **local**: `./assets/images/login-background.png`
   * - **url externa**: `https://thf.totvs.com.br/assets/images/login-background.png`
   *
   * > Essa propriedade Ã© ignorada para aplicaÃ§Ãµes mobile.
   */
  @Input('t-background') background?: string;

  /**
   * @optional
   *
   * @description
   *
   * Personaliza o e-mail que Ã© exibido na mensagem de dica de login padrÃ£o para contato de suporte.
   */
  @Input('t-contact-email') set contactEmail(value: string) {
    const language = this.selectedLanguage || browserLanguage();
    this._contactEmail = value;

    this.setLoginHintLiteral(language, value);
  }
  get contactEmail() {
    return this._contactEmail;
  }

  /**
   * @optional
   *
   * @description
   *
   * Valor customizado que sucede o tÃ­tulo de boas-vindas.
   *
   * > Esta propriedade Ã© sobreposta se `t-literals` contiver uma definiÃ§Ã£o customizada para a literal `title`.
   *
   * > Veja mais sobre as literais na propriedade `t-literals`.
   *
   */
  @Input('t-product-name') set productName(value: string) {
    const language = this.selectedLanguage || browserLanguage();
    this._productName = value;

    this.setTitleLiteral(language, value);
  }
  get productName() {
    return this._productName;
  }

  /**
   * @optional
   *
   * @description
   * Adiciona uma `tag` abaixo do tÃ­tulo que especifica o ambiente que o usuÃ¡rio estÃ¡ fazendo o login.
   *
   * > Essa propriedade limita o texto em 40 caracteres.
   */
  @Input('t-environment') set environment(environment: string) {
    if (environment && environment.length > thfPageLoginContentMaxLength) {
      this._environment = environment.substring(0, thfPageLoginContentMaxLength);
    } else {
      this._environment = environment;
    }
  }
  get environment() {
    return this._environment;
  }

  /**
   * @optional
   *
   * @description
   * Exibe um aviso de bloqueio de acordo com a quantidade restante de tentativas.
   * O aviso serÃ¡ exibido somente se a quantidade for maior que zero.
   *
   * > Caso tenha algum valor atribuÃ­do para o atributo `t-authentication-url` e o retorno da requisiÃ§Ã£o estiver atribuindo valor
   * para o `t-exceeded-attempts-warning`, o valor considerado serÃ¡ o do retorno da requisiÃ§Ã£o.
   *
   * @default `0`
   */
  @Input('t-exceeded-attempts-warning') set exceededAttemptsWarning(value: number) {
    this._exceededAttemptsWarning = convertToInt(value);
    this.showExceededAttemptsWarning = this.exceededAttemptsWarning > 0;
  }

  get exceededAttemptsWarning(): number {
    return this._exceededAttemptsWarning;
  }

  /**
   * @optional
   *
   * @description
   *
   * Esconde a funÃ§Ã£o "Lembrar usuÃ¡rio" do formulÃ¡rio de login.
   *
   * Quando essa propriedade Ã© setada com `true` a propriedade `rememberUser` enviada no evento `t-login-submit` serÃ¡ sempre
   * `false`.
   *
   * > Veja a propriedade `t-literals` para customizar a literal `rememberUser`.
   *
   * @default `false`
   */
  @Input('t-hide-remember-user') set hideRememberUser(value: boolean) {
    this._hideRememberUser = <any>value === '' ? true : convertToBoolean(value);

    if (this._hideRememberUser) {
      this.rememberUser = false;
    }
  }
  get hideRememberUser(): boolean {
    return this._hideRememberUser;
  }

  /**
   * @optional
   *
   * @description
   *
   * Objeto com as literais usadas no `thf-page-login`.
   *
   * Existem duas maneiras de customizar o componente, passando um objeto com todas as literais disponÃ­veis:
   *
   * ```
   *  const customLiterals: ThfPageLoginLiterals = {
   *    title: 'Seja bem-vindo',
   *    loginErrorPattern: 'Login obrigatÃ³rio',
   *    loginHint: 'Caso nÃ£o possua usuÃ¡rio entre em contato com o suporte',
   *    loginLabel: 'Insira seu usuÃ¡rio',
   *    loginPlaceholder: 'Insira seu usuÃ¡rio de acesso',
   *    passwordErrorPattern: 'Senha obrigatÃ³ria',
   *    passwordLabel: 'Insira sua senha',
   *    passwordPlaceholder: 'Insira sua senha de acesso'
   *    customFieldErrorPattern: 'Campo customizado invÃ¡lido',
   *    customFieldPlaceholder: 'Por favor insira um valor',
   *    rememberUser: 'Lembrar usuÃ¡rio',
   *    rememberUserHint: 'Esta opÃ§Ã£o pode ser desabilitada nas configuraÃ§Ãµes do sistema',
   *    submitLabel: 'Acessar sistema',
   *    forgotPassword: 'Esqueceu sua senha?'
   *  };
   * ```
   *
   * Ou passando apenas as literais que deseja customizar:
   *
   * ```
   *  const customLiterals: ThfPageLoginLiterals = {
   *    loginPlaceholder: 'Insira seu usuÃ¡rio de acesso',
   *    passwordPlaceholder: 'Insira sua senha de acesso',
   *    submitLabel: 'Acessar sistema'
   *  };
   * ```
   *
   * E para carregar as literais customizadas, basta apenas passar o objeto para o componente.
   *
   * ```
   * <thf-page-login
   *   [t-literals]="customLiterals">
   * </thf-page-login>
   * ```
   *
   *  > O objeto padrÃ£o de literais serÃ¡ traduzido de acordo com o idioma do browser (pt, en, es).
   *  > Ã tambÃ©m possÃ­vel alternar o objeto padrÃ£o de literais atravÃ©s do seletor de idiomas localizado na parte inferior do template.
   */
  @Input('t-literals') set literals(value: ThfPageLoginLiterals) {
    const language = this.selectedLanguage || browserLanguage();

    if (value) {
      this.getLiterals(language, value);
      this.containsCustomLiterals =
        !this.literals.title.includes(thfPageLoginLiteralsDefault[language].title) ||
        !this.literals.loginHint.includes(thfPageLoginLiteralsDefault[language].loginHint);
    } else {
      this.containsCustomLiterals = false;
      this._literals = thfPageLoginLiteralsDefault[language];
    }
  }

  get literals() {
    return this._literals || thfPageLoginLiteralsDefault[browserLanguage()];
  }

  /**
   * @optional
   *
   * @description
   *
   * Habilita um estado de carregamento ao botÃ£o de *login*.
   *
   * > Ã necessÃ¡rio atribuir `true` Ã  esta propriedade na funÃ§Ã£o definida em `t-login-submit`.
   *
   * @default `false`
   */
  @Input('t-loading') set loading(value: boolean) {
    this._loading = convertToBoolean(value);
  }

  get loading(): boolean {
    return this._loading;
  }

  /**
   * @optional
   *
   * @description
   *
   * Valor do modelo do campo de login.
   *
   */
  @Input('t-login') set login(value: string) {
    this._login = value;

    if (!this.authenticationUrl) {
      this.loginChange.emit(this._login);
    }
  }

  get login(): string {
    return this._login;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caminho para a logomarca localizada na parte superior.
   *
   * > Caso seja indefinida o espaÃ§o se mantÃ©m preservado porÃ©m vazio.
   */
  @Input('t-logo') logo?: string;

  /**
   * @optional
   *
   * @description
   *
   * ExpressÃ£o regular para validar o campo de login, caso a expressÃ£o nÃ£o seja atentida, a literal `loginErrorPattern`
   * serÃ¡ exibida.
   *
   * Exemplos de valores vÃ¡lidos:
   * - email: `[expressao-regular-email]`
   * - cpf: `[expressao-regular-cpf]`
   *
   * > Veja a propriedade `t-literals` para customizar a literal `loginErrorPattern`.
   */
  @Input('t-login-pattern') loginPattern?: string;

  /**
   * @optional
   *
   * @description
   *
   * Atributo que recebe uma lista de erros e exibe abaixo do campo de login.
   */
  @Input('t-login-errors') set loginErrors(value: Array<string>) {
    this._loginErrors = value || [];
    this.setLoginErrors(this._loginErrors);
  }
  get loginErrors() {
    return this._loginErrors;
  }

  /**
   * @optional
   *
   * @description
   *
   * Atributo que recebe uma lista de erros e exibe abaixo do campo de password.
   */
  @Input('t-password-errors') set passwordErrors(value: Array<string>) {
    this._passwordErrors = value || [];
    this.setPasswordErrors(this._passwordErrors);
  }
  get passwordErrors() {
    return this._passwordErrors;
  }

  /**
   * @optional
   *
   * @description
   *
   * ExpressÃ£o regular para validar o campo de password, caso a expressÃ£o nÃ£o seja atentida, a literal `passwordErrorPattern`
   * serÃ¡ exibida.
   *
   * Exemplos de valores vÃ¡lidos:
   * - Apenas nÃºmeros: `\d?`
   * - Letras mÃ­nusculas: `\z?`
   *
   * > Veja a propriedade `t-literals` para customizar a literal `passwordErrorPattern`.
   */
  @Input('t-password-pattern') passwordPattern?: string;

  /**
   * @optional
   *
   * @description
   *
   * Exibe um link abaixo do formulÃ¡rio de login para que os usuÃ¡rios da aplicaÃ§Ã£o faÃ§am a recuperaÃ§Ã£o dos dados de autenticaÃ§Ã£o.
   *
   * A propriedade aceita os seguintes tipos:
   *
   * - **String**: informe uma url externa ou uma rota vÃ¡lida;
   * - **Function**: pode-se customizar a aÃ§Ã£o. Para esta possilidade basta atribuir:
   * ```
   * <thf-page-login>
   *   [recovery]="this.funcaominha.bind(this)";
   * </thf-page-login>
   * ```
   *
   * - **ThfPageLoginRecovery**: cria-se vÃ­nculo automÃ¡tico com o template **thf-modal-password-recovery**.
   *   O objeto deve conter a **url** para requisiÃ§Ã£o dos recursos e pode-se definir o **tipo** de modal para recuperaÃ§Ã£o de senha,
   *   **email** para contato e **mÃ¡scara** do campo de telefone.
   */
  @Input('t-recovery') set recovery(value: string | Function | ThfPageLoginRecovery) {
    this._recovery = value;
  }

  get recovery() {
    return this._recovery;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caso a aplicaÃ§Ã£o tenha um link para novos cadastros, informe uma url externa ou uma rota vÃ¡lida, dessa
   * forma serÃ¡ exibido um link abaixo do formulÃ¡rio de login para os usuÃ¡rios da aplicaÃ§Ã£o.
   *
   * Exemplos de valores vÃ¡lidos:
   * - **local**: `/home`
   * - **url externa**: `https://thf.totvs.com.br`
   *
   * > Veja a propriedade `t-literals` para customizar a literal `registerUrl`.
   */
  @Input('t-register-url') set registerUrl(value: string) {
    this._registerUrl = isTypeof(value, 'string') ? value : undefined;
  }

  get registerUrl(): string {
    return this._registerUrl;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caminho para a logomarca localizada no rodapÃ©.
   */
  @Input('t-secondary-logo') secondaryLogo?: string;

  /**
   * @optional
   *
   * @description
   *
   * Atributo que recebe o tipo de esquema da autenticaÃ§Ã£o, sendo suportados apenas os valores `Basic` e `Bearer`.
   *
   * > Caso o tipo definido seja `Basic`, o componente farÃ¡ uma requisiÃ§Ã£o `POST` contendo:
   *
   * ```
   * headers {
   *  Authorization: Basic base64(login:password)
   * }
   *
   * body {
   *  rememberUser: rememberUser
   * }
   * ```
   *
   * > Caso o tipo definido seja `Bearer`, o componente farÃ¡ uma requisiÃ§Ã£o `POST` contendo:
   *
   * ```
   * body {
   *  login: login,
   *  password: base64(password)
   *  rememberUser: rememberUser
   * }
   * ```
   *
   * @default `ThfPageLoginAuthenticationType.Basic`
   */
  @Input('t-authentication-type') set authenticationType(value: ThfPageLoginAuthenticationType) {
    this._authenticationType =
    (<any>Object).values(ThfPageLoginAuthenticationType).includes(value) ? value : ThfPageLoginAuthenticationType.Basic;
  }

  get authenticationType(): ThfPageLoginAuthenticationType {
    return this._authenticationType;
  }

  /**
   * @optional
   *
   * @description
   *
   * Endpoint usado pelo template para requisiÃ§Ã£o do recurso. Quando preenchido, o mÃ©todo `t-login-submit` serÃ¡ ignorado e o
   * componente adquirirÃ¡ automatizaÃ§Ã£o para o processo de autenticaÃ§Ã£o.
   *
   * ### Processos
   * Ao digitar um valor vÃ¡lido no campo de login/password e pressionar **Enter**, o componente farÃ¡ uma requisiÃ§Ã£o `POST`
   * na url especificada nesta propriedade passando o objeto contendo o valor definido pelo usuÃ¡rio:
   *
   * ```
   * headers {
   *  Authorization: Basic base64(login:password)
   * }
   *
   * body {
   *  rememberUser: rememberUser
   * }
   * ```
   *
   * Em caso de **sucesso**, o objeto de retorno Ã© armazenado no `sessionStorage` e o usuÃ¡rio Ã© redirecionado para a pÃ¡gina inicial da
   * aplicaÃ§Ã£o `/`.
   *
   * ```
   * 200:
   *  {
   *    user: user
   *  }
   * ```
   *
   * Em caso de **erro** na autenticaÃ§Ã£o, espera-se o seguinte retorno:
   *
   * ```
   * 400/401
   *  {
   *    code: 400/401,
   *    message: message,
   *    detailedMessage: detailedMessage,
   *    helpUrl?: helpUrl
   *  }
   * ```
   *
   * > Pode-se atribuir uma quantidade mÃ¡xima de tentativas restantes (maxAttemptsRemaining) para o atributo `t-exceeded-attempts-warning`,
   * assim como os avisos relacionados aos campos login e password (loginWarnings, passwordWarnings) para os atributos `t-login-errors` e
   * `t-password-errors` conforme retorno abaixo:
   *
   * ```
   * 400
   *  {
   *    code: 400/401,
   *    message: message,
   *    detailedMessage: detailedMessage,
   *    helpUrl?: helpUrl,
   *    maxAttemptsRemaining?: maxAttemptsRemaining,
   *    loginWarnings?: [loginWarnings],
   *    passwordWarnings?: [passwordWarnings],
   *  }
   * ```
   *
   * > Caso o valor atribuÃ­do para `t-exceeded-attempts-warning` seja igual a 0(zero), poderÃ¡ ser passado um valor para o
   * atributo `t-blocked-url` e o usuÃ¡rio serÃ¡ redirecionado para uma tela de bloqueio.
   *
   * *Processo finalizado.*
   *
   * _______________
   *
   * #### Praticidade
   * As informaÃ§Ãµes do serviÃ§o de autenticaÃ§Ã£o tambÃ©m podem ser transmitidas diretamente pelas configuraÃ§Ã£os de rota e, desta maneira,
   * dispensa-se qualquer menÃ§Ã£o e/ou importaÃ§Ã£o do componente `thf-page-login` no restante da aplicaÃ§Ã£o. O exemplo abaixo exemplifica
   * a forma dinÃ¢mica com a qual o template de tela de login pode ser gerado ao navegar para rota `/login`, e tambÃ©m como ele se comunica
   * com o serviÃ§o para efetuaÃ§Ã£o do processo de autenticaÃ§Ã£o do usuÃ¡rio e solicitaÃ§Ã£o de nova senha.
   * Basta definir nas configuraÃ§Ãµes de rota:
   *
   *
   * ```
   *   import { ThfPageLoginComponent, ThfPageLoginAthenticationType } from '@totvs/thf-templates';
   *
   *   ...
   *   const routes: Routes = [
   *     {
   *       path: 'login', component: ThfPageLoginComponent, data: {
   *         serviceApi: 'https://thf.totvs.com.br/sample/api/users/authentication',
   *         environment: 'development',
   *         recovery: {
   *           url: 'https://thf.totvs.com.br/sample/api/users',
   *           type: ThfModalPasswordRecoveryType.All,
   *           contactMail: 'dev.totvs@totvs.com',
   *           phoneMask: '9-999-999-9999'
   *         },
   *         registerUrl: '/new-password',
   *         authenticationType: ThfPageLoginAthenticationType.Basic
   *       }
   *     }
   *     ...
   *   ];
   *
   *   @NgModule({
   *     imports: [RouterModule.forRoot(routes)],
   *     exports: [RouterModule]
   *   })
   *   export class AppRoutingModule { }
   * ```
   *
   *
   * O metadado `serviceApi` deve ser a **url** para requisiÃ§Ã£o dos recursos de autenticaÃ§Ã£o, o `environment` alimenta a propriedade
   * `t-environment`, `recovery` Ã© a interface `ThfPageLoginRecovery` responsÃ¡vel pelas especificaÃ§Ãµes contidas na modal de recuperaÃ§Ã£o de
   * senha, `registerUrl` alimenta a propriedade `t-register-url` e `authenticationType` que define a propriedade `t-authentication-type`.
   *
   * > Ã essencial que siga a nomenclatura dos atributos exemplificados acima para sua efetiva funcionalidade.
   *
   */
  @Input('t-authentication-url') set authenticationUrl(value: string) {
    this._authenticationUrl = isTypeof(value, 'string') ? value : undefined;
  }

  get authenticationUrl(): string {
    return this._authenticationUrl;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caso o valor atribuÃ­do para `t-exceeded-attempts-warning` seja igual a 0(zero) e a aplicaÃ§Ã£o tenha um link de bloqueio de usuÃ¡rio,
   * informe uma url externa ou uma rota vÃ¡lida, dessa forma em caso de bloqueio o usuÃ¡rio serÃ¡ redirecionado.
   */
  @Input('t-blocked-url') set blockedUrl(value: string) {
    this._blockedUrl = isTypeof(value, 'string') ? value : undefined;
  }

  get blockedUrl(): string {
    return this._blockedUrl;
  }

  /**
   * Evento disparado quando o usuÃ¡rio alterar o input do campo login.
   *
   * Esse evento receberÃ¡ como parÃ¢metro uma variÃ¡vel do tipo `string` com o texto informado no campo.
   *
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-authentication-url`.
   */
  @Output('t-login-change') loginChange?: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Evento disparado ao submeter o formulÃ¡rio de login (apertando `Enter` dentro dos campos ou pressionando o botÃ£o de confirmaÃ§Ã£o).
   *
   * Esse evento receberÃ¡ como parÃ¢metro um objeto do tipo `ThfPageLogin` com os dados informados no formulÃ¡rio.
   *
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-url-recovery`.
   *
   * > Para mais detalhes consulte a documentaÃ§Ã£o sobre a interface `ThfPageLogin` mais abaixo.
   */
  @Output('t-login-submit') loginSubmit = new EventEmitter<ThfPageLogin>();

  /**
   * Evento disparado quando o usuÃ¡rio alterar o input do campo password.
   *
   * Esse evento receberÃ¡ como parÃ¢metro uma variÃ¡vel do tipo `string` com o texto informado no campo.
   *
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-authentication-url`.
   */
  @Output('t-password-change') passwordChange?: EventEmitter<string> = new EventEmitter<string>();

  constructor(private loginService: ThfPageLoginService, public router: Router) { }

  ngOnDestroy() {
    if (this.loginSubscription) {
      this.loginSubscription.unsubscribe();
    }
  }

  closePopover() {
    this.showExceededAttemptsWarning = false;
  }

  getLiterals(language?: string, value?) {
    language = language || browserLanguage();

    if (value instanceof Object && !(value instanceof Array)) {
      this.customizedDefaultLiterals = {
        ...this.customizedDefaultLiterals,
        ...value
      };

      this._literals = {
        ...thfPageLoginLiteralsDefault[thfLocaleDefault],
        ...thfPageLoginLiteralsDefault[language],
        ...this.customizedDefaultLiterals,
        ...value
      };
    } else {
      this.containsCustomLiterals = false;
      this._literals = thfPageLoginLiteralsDefault[language];
    }
  }

  onLoginSubmit(): void {
    const loginForm: ThfPageLogin = {
      login: this.login,
      password: this.password,
      rememberUser: this.rememberUser
    };

    if (this.customField) {
      loginForm[this.customFieldObject.property] = this.customFieldObject.value;
    }

    if (this.authenticationUrl) {
      this.loginSubscription =
      this.loginService.onLogin(this.authenticationUrl, this.authenticationType, loginForm)
      .subscribe(data => {
        this.setValuesToProperties();
        sessionStorage.setItem('THF_USER_LOGIN', JSON.stringify(data));
        this.openInternalLink('/');
      },
      error => {
        if (error.error.code === '400' || error.error.code === '401') {
          this.setValuesToProperties(error);
          this.redirectBlockedUrl(this.exceededAttemptsWarning, this.blockedUrl);
        }
      });
    } else {
      this.loginSubmit.emit(loginForm);
      this.showExceededAttemptsWarning = this.exceededAttemptsWarning > 0;
    }
  }

  setLoginHintLiteral(language: string, value: string) {
    const defaultLoginHintLiteral = thfPageLoginLiteralsDefault[language].loginHint;
    const prepositionLiteral = thfPageLoginLiteralIn[language];
    if (value) {
      this.concatenateLiteral(value, 'loginHint', defaultLoginHintLiteral, prepositionLiteral);
    } else if (!value) {
      this.literals = { loginHint: defaultLoginHintLiteral };
    }
  }

  setTitleLiteral(language: string, value: string) {
    const defaultTitleLiteral = thfPageLoginLiteralsDefault[language].title;
    const prepositionLiteral = thfPageLoginLiteralTo[language];
    if (value) {
      this.concatenateLiteral(value, 'title', defaultTitleLiteral, prepositionLiteral);
    } else if (!value && this.literals.title.includes(defaultTitleLiteral)) {
        this.literals = { title: defaultTitleLiteral };
      }
  }

  private concatenate(defaultLiteral: string, prefixLiteral: string, value: string) {
    return `${defaultLiteral} ${prefixLiteral} ${value}`;
  }

  private concatenateLiteral(value: string, literal: string, defaultLiteral: string, prepositionLiteral: string) {
    if (value && this.literals[literal].includes(defaultLiteral) || this.literals[literal].includes(value)) {
      this.literals = {
        [literal]: this.concatenate(defaultLiteral, prepositionLiteral, value)
      };
    }
  }

  private getDefaultCustomFieldObject(property): ThfPageLoginCustomField {
    return { property };
  }

  private openExternalLink(url: string) {
    window.open(url, '_blank');
  }

  private openInternalLink(url: string) {
    this.router.navigate([url]);
  }

  private redirectBlockedUrl(attempts: number, blockedUrl: string) {
    if (attempts === 0 && blockedUrl) {
      this.showExceededAttemptsWarning = false;
      isExternalLink(blockedUrl) ? this.openExternalLink(blockedUrl) : this.openInternalLink(blockedUrl);
    }
  }

  private setValuesToProperties(result?) {
    if (result) {
      this.exceededAttemptsWarning = result.error.maxAttemptsRemaining;
      this.loginErrors = result.error.loginWarnings;
      this.passwordErrors = result.error.passwordWarnings;
      this.blockedUrl = result.error.blockedUrl;
    } else {
      this.exceededAttemptsWarning = 0;
      this.loginErrors = [];
      this.passwordErrors = [];
      this.blockedUrl = '';
    }
  }

  protected abstract setLoginErrors(value: Array<string>): void;

  protected abstract setPasswordErrors(value: Array<string>): void;
}
